import { LOCALES, useI18n } from "@/i18n";
import { localize, useRoute } from "@/lib/router";
import { cn } from "@/lib/utils";

/**
 * Switches the interface language. The exam content is unaffected — tasks stay
 * in Polish, which is the language being examined.
 *
 * Each option is a real link (`/uk/telc`), so a crawler and a reader with no
 * script both reach the other languages; a plain click is still handled in-app.
 *
 * A segmented control rather than a dropdown: with three options it is one tap
 * instead of two, and it needs no portal or floating-position maths. The
 * dropdown version had the menu land off-screen because Floating UI never
 * recomputed its position here, which is a lot of machinery to go wrong for
 * three buttons.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { locale, setLocale, t } = useI18n();
  // Subscribing keeps every `href` on the page being looked at.
  const route = useRoute();

  return (
    <div
      role="group"
      aria-label={t("nav.language")}
      className={cn("flex items-center gap-0.5 rounded-md bg-muted p-0.5", className)}
    >
      {LOCALES.map((option) => {
        const active = option.id === locale;
        return (
          <a
            key={option.id}
            href={localize(route, option.id)}
            lang={option.id}
            hrefLang={option.id}
            aria-current={active ? "true" : undefined}
            title={option.name}
            onClick={(e) => {
              // Let the browser have new-tab and modified clicks.
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
              e.preventDefault();
              setLocale(option.id);
            }}
            className={cn(
              "rounded px-1.5 py-1 text-xs font-bold transition-colors",
              "focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
              active
                ? "bg-background text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option.short}
          </a>
        );
      })}
    </div>
  );
}
